import { Status, NonClientError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';

import useQueryParameters from 'hooks/useQueryParameters';

import catchOnly from 'utils/catchOnly';

const PATH = '/api/financing_google_wallet_provision';

type FinancingGoogleWalletProvisionHook = () => [
  (
    params: FinancingGoogleWalletProvisionRequest,
  ) => Promise<FinancingGoogleWalletProvisionResult>,
  Status,
  boolean,
];

export interface FinancingGoogleWalletProvisionRequest {
  serverSessionId: string;
  clientSessionId: string;
  // FIXME: double check the types for these
  tokenSetting: number;
  cardSetting: number;
  publicWalletId: string;
  publicDeviceId: string;
  issuanceId: string;
}

export interface FinancingGoogleWalletProvisionResult {
  walletId: string;
  state?: string;
  jws?: {};
}

export class FinancingGoogleWalletProvisionError extends Error {}

const useFinancingGoogleWalletProvision: FinancingGoogleWalletProvisionHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { mockFGWP } = useQueryParameters();

  const financingGoogleWalletProvisionRequest = useCallback(
    async (
      params: FinancingGoogleWalletProvisionRequest,
    ): Promise<FinancingGoogleWalletProvisionResult> => {
      setError(false);

      try {
        const responsePromise = fetch(PATH, {
          method: 'POST',
          body: {
            ...params,
            mockResponse: mockFGWP,
          },
        });
        const response = await responsePromise;

        if (response.ok) {
          const result: FinancingGoogleWalletProvisionResult = await response.json();
          return Promise.resolve(result);
        } else {
          setError(true);
          return Promise.reject(new FinancingGoogleWalletProvisionError());
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          setError(e.message);
          return Promise.reject(e);
        }
        // Re-throw so that any exceptions can be handled downstream.
        throw e;
      }
    },
    [fetch, mockFGWP],
  );

  return [financingGoogleWalletProvisionRequest, status, error];
};

export default useFinancingGoogleWalletProvision;
